/* Variant selection + gallery sync for the product page. Vanilla JS, no build step. */
(() => {
  'use strict';

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form[data-product-form]');
      this.variants = JSON.parse(this.querySelector('[data-variants-json]').textContent);
      this.addEventListener('change', () => this.onVariantChange());
    }

    onVariantChange() {
      const selectedOptions = Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((el) => el.value);
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i]));

      const priceEl = document.querySelector('[data-product-price]');
      const compareEl = document.querySelector('[data-product-compare-price]');
      const idInput = this.form.querySelector('input[name="id"]');
      const submitBtn = this.form.querySelector('[type="submit"]');
      const gallery = document.querySelector('[data-product-gallery]');

      if (!variant) {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.querySelector('[data-add-label]').textContent = window.IWSStrings.unavailable;
        }
        return;
      }

      idInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }

      if (submitBtn) {
        const label = submitBtn.querySelector('[data-add-label]');
        if (variant.available) {
          submitBtn.disabled = false;
          label.textContent = window.IWSStrings.addToCart;
        } else {
          submitBtn.disabled = true;
          label.textContent = window.IWSStrings.soldOut;
        }
      }

      if (gallery && variant.featured_media_id) {
        const target = gallery.querySelector(`[data-media-id="${variant.featured_media_id}"]`);
        if (target) target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  function formatMoney(cents) {
    return window.IWSMoneyFormat
      ? window.IWSMoneyFormat.replace('{{amount}}', (cents / 100).toFixed(2))
      : `$${(cents / 100).toFixed(2)}`;
  }
  window.IWSFormatMoney = formatMoney;

  document.querySelectorAll('[data-qty-input]').forEach((wrapper) => {
    const input = wrapper.querySelector('input[type="number"]');
    wrapper.querySelectorAll('[data-qty-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const delta = btn.dataset.qtyBtn === 'plus' ? 1 : -1;
        input.value = Math.max(1, parseInt(input.value || '1', 10) + delta);
      });
    });
  });

  document.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-product-gallery]');
      const target = gallery.querySelector(`[data-media-id="${thumb.dataset.galleryThumb}"]`);
      if (target) target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    });
  });

  /* ---------------------------------------------------------------------
   * Gallery zoom lightbox
   * ------------------------------------------------------------------- */
  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lightbox-track]');
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.step(-1));
      this.querySelector('[data-lightbox-next]').addEventListener('click', () => this.step(1));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.step(-1);
        if (e.key === 'ArrowRight') this.step(1);
      });
    }

    openAt(index, images) {
      this.images = images;
      this.index = index;
      this.render();
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    step(delta) {
      if (!this.images || !this.images.length) return;
      this.index = (this.index + delta + this.images.length) % this.images.length;
      this.render();
    }

    render() {
      if (!this.images || !this.images.length) return;
      this.track.innerHTML = '';
      const img = document.createElement('img');
      img.src = this.images[this.index].src;
      img.alt = this.images[this.index].alt || '';
      this.track.appendChild(img);
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }
  if (!customElements.get('product-lightbox')) customElements.define('product-lightbox', ProductLightbox);

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const lightbox = gallery.parentElement.querySelector('product-lightbox');
    if (!lightbox) return;

    const slides = Array.from(gallery.querySelectorAll('[data-media-id] img'));
    const images = slides.map((img) => ({ src: img.currentSrc || img.src, alt: img.alt }));

    gallery.querySelectorAll('[data-zoom-trigger]').forEach((btn, i) => {
      btn.addEventListener('click', () => lightbox.openAt(i, images));
    });
  });

  /* ---------------------------------------------------------------------
   * Share button — native Web Share API, clipboard-copy fallback
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-share-button]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const shareData = { title: btn.dataset.shareTitle || document.title, url: window.location.href };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (e) {
          /* user cancelled */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(shareData.url);
        const original = btn.innerHTML;
        btn.setAttribute('aria-label', 'Link copied');
        btn.classList.add('is-copied');
        setTimeout(() => {
          btn.innerHTML = original;
          btn.setAttribute('aria-label', 'Share');
          btn.classList.remove('is-copied');
        }, 1600);
      } catch (e) {
        window.prompt('Copy this link', shareData.url);
      }
    });
  });

  /* ---------------------------------------------------------------------
   * Sticky buy bar — appears once the main buy box scrolls out of view
   * ------------------------------------------------------------------- */
  const buybox = document.querySelector('[data-buybox]');
  const stickyBar = document.querySelector('[data-sticky-bar]');
  if (buybox && stickyBar && 'IntersectionObserver' in window) {
    stickyBar.hidden = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    observer.observe(buybox);

    const stickyAddBtn = stickyBar.querySelector('[data-sticky-bar-add]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', () => {
        const idInput = document.querySelector('form[data-product-form] input[name="id"]');
        const variantId = idInput ? idInput.value : stickyAddBtn.dataset.variantId;
        window.IWSAddToCart({ id: variantId, quantity: 1 }, stickyAddBtn);
      });
    }
  }
})();
